<script lang="ts">
    /**
     * LinkedIn-style title search: type a job title, pick from suggestions, collect chips.
     *
     * The chips are the search. Each one is a job_roles code, and the form submits them as
     * ?roles=a|b — so results come from what a job IS rather than from words matching its title.
     * Free text still works: anything left in the box submits as ?keyword= and the server either
     * resolves it to a chip or falls back to full-text search.
     *
     * Suggestions come from the server one prefix at a time. The vocabulary is deliberately NOT
     * shipped to the browser — see TitlesApiController — so this asks for what was typed and nothing
     * more.
     */
    import { onMount } from 'svelte';
    import { analytics } from './analytics';

    /** `via` is what was typed to reach the chip — the phrasing, kept for the submit event. */
    interface Chip { code: string; name: string; via?: string; }
    interface Suggestion { code: string; name: string; alias: string; group?: string | null; }

    // label/placeholder are overridable because this island is mounted on two pages that frame the
    // same control differently: the home page is searching, /find-jobs is naming your job title.
    // requireSelection turns the box from a search into a selection. On the home page free text is a
    // real query (full-text search), so Enter submits it. On /find-jobs the chips ARE the request —
    // the server can only run on a role — so raw text has nothing to mean, and Enter picks, waits,
    // or explains instead. See resolveOrExplain.
    let { selected = [], keyword = '', label = 'search', placeholder = 'title or keyword', requireSelection = false }:
        { selected?: Chip[]; keyword?: string; label?: string; placeholder?: string; requireSelection?: boolean } = $props();

    let chips = $state<Chip[]>([...selected]);
    let query = $state(keyword);
    let open = $state(false);
    let activeIndex = $state(-1);
    let suggestions = $state<Suggestion[]>([]);
    /** The query the current suggestions answer, so a pick never lands on a list the typing has outrun. */
    let suggestionsFor = '';
    /** The line under the box: a miss, or the nudge when nothing has been picked yet. */
    let hint = $state('');
    /** True while Enter waits on suggestions it asked for; a second Enter meanwhile does nothing. */
    let resolving = false;

    /** Shortest query the server will answer; asking below it just returns nothing. */
    const MIN_QUERY = 2;
    const DEBOUNCE_MS = 180;

    /**
     * Prefixes already answered this session. Typing forward then backspacing is the single most
     * common typeahead motion, and without this it would re-ask the server for answers it just gave.
     */
    const cache = new Map<string, Suggestion[]>();

    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    let inFlight: AbortController | undefined;
    /** Guards against a slow early response landing after a faster later one and overwriting it. */
    let latestQuery = '';

    let inputEl: HTMLInputElement | null = $state(null);
    let boxEl: HTMLDivElement | null = $state(null);

    /**
     * How many suggestions actually fit between the box and the top of the on-screen keyboard.
     *
     * The guidance is that every suggestion offered should be visible without scrolling — a list you
     * have to scroll inside is harder to use than a shorter one. On a phone the keyboard leaves
     * roughly 140px under the input, which is two rows out of the eight the server sends, and the
     * other six are hidden with no hint they exist (the keyboard clips the list, so its own scrollbar
     * never shows). So the list is capped to what fits rather than overflowing out of sight.
     */
    const ROW_HEIGHT_PX = 52;
    /** A one-item list is worse than a short scroll; below this, allow scrolling instead. */
    const MIN_VISIBLE = 3;
    let fitCount = $state(8);
    let fitMaxHeight = $state(260);

    /**
     * The keyboard shrinks the VISUAL viewport and leaves the layout viewport alone, so
     * window.innerHeight can't see it — visualViewport can. Falls back to innerHeight where the API
     * is missing (and on desktop, where there is no keyboard to account for).
     */
    function measureFit() {
        if (!boxEl) return;
        const vv = window.visualViewport;
        const viewportBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
        const available = viewportBottom - boxEl.getBoundingClientRect().bottom - 12;
        fitMaxHeight = Math.max(ROW_HEIGHT_PX, available);
        fitCount = Math.max(MIN_VISIBLE, Math.min(8, Math.floor(available / ROW_HEIGHT_PX)));
    }

    /** What actually renders: the server's list trimmed to what the screen can show. */
    const visible = $derived(suggestions.slice(0, fitCount));

    $effect(() => {
        measureFit();
        const vv = window.visualViewport;
        vv?.addEventListener('resize', measureFit);
        vv?.addEventListener('scroll', measureFit);
        window.addEventListener('resize', measureFit);
        return () => {
            vv?.removeEventListener('resize', measureFit);
            vv?.removeEventListener('scroll', measureFit);
            window.removeEventListener('resize', measureFit);
        };
    });

    /** The cap the server also enforces; showing it beats silently dropping a pick. */
    const MAX_CHIPS = 10;

    /**
     * Push the selection into the form's existing hidden roles field rather than rendering a second
     * one. The server writes that field as the no-JS fallback; two inputs of the same name would
     * submit ?roles=a&roles= and let the empty one win, silently dropping the whole search.
     */
    $effect(() => {
        const hidden = document.getElementById('roles') as HTMLInputElement | null;
        if (hidden) hidden.value = chips.map(c => c.code).join('|');
        // Two facts the page around the island wants: whether a role has been picked (its button
        // reads as waiting until one has), and the phrasings that reached the chips, so the submit
        // event can carry what people actually call these jobs.
        const form = hidden?.form;
        if (form) {
            form.setAttribute('data-has-roles', chips.length ? '1' : '0');
            form.setAttribute('data-typed', chips.map(c => safeTerm(c.via || '')).filter(Boolean).join('|'));
        }
    });

    /**
     * The second line of a suggestion: the canonical role the typed phrase resolves to (omitted when
     * the phrase IS the role name), then the job function it belongs to. Kept off the title's line
     * because a real posting title plus both labels runs far past the width of a phone.
     */
    function metaLine(s: Suggestion): string {
        const parts: string[] = [];
        if (s.alias.toLowerCase() !== s.name.toLowerCase()) parts.push(s.name);
        if (s.group) parts.push(s.group);
        return parts.join(' · ');
    }

    /** Drop anything already picked, so a chosen chip never reappears in the list. */
    function withoutChosen(list: Suggestion[]): Suggestion[] {
        const chosen = new Set(chips.map(c => c.code));
        return list.filter(s => !chosen.has(s.code));
    }

    async function fetchSuggestions(raw: string) {
        const q = raw.trim().toLowerCase();
        latestQuery = q;

        if (q.length < MIN_QUERY) {
            suggestions = [];
            suggestionsFor = q;
            return;
        }
        const cached = cache.get(q);
        if (cached) {
            suggestions = withoutChosen(cached);
            suggestionsFor = q;
            return;
        }

        inFlight?.abort();
        const controller = new AbortController();
        inFlight = controller;
        try {
            const res = await fetch(`/api/titles?q=${encodeURIComponent(q)}`, { signal: controller.signal });
            if (!res.ok) throw new Error('suggestions unavailable');
            const data = await res.json();
            const list: Suggestion[] = data.suggestions || [];
            cache.set(q, list);
            // A response for a prefix the user has already typed past is worse than none.
            if (latestQuery === q) { suggestions = withoutChosen(list); suggestionsFor = q; }
        } catch (err) {
            if ((err as Error).name === 'AbortError') return;
            // Suggestions are an enhancement — the box still submits as a plain keyword search.
            if (latestQuery === q) { suggestions = []; suggestionsFor = q; }
        }
    }

    function onQueryChanged() {
        open = true;
        activeIndex = -1;
        hint = '';
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => fetchSuggestions(query), DEBOUNCE_MS);
    }

    function addChip(code: string, name: string, via = '') {
        if (chips.length >= MAX_CHIPS || chips.some(c => c.code === code)) return;
        chips = [...chips, { code, name, via }];
        query = '';
        suggestions = [];
        suggestionsFor = '';
        open = false;
        activeIndex = -1;
        hint = '';
        inputEl?.focus();
    }

    /**
     * A typed phrase reduced to something safe to file: lower-cased so two spellings of one title
     * land on one row, short enough that a pasted paragraph cannot bloat an event, and dropped when
     * it holds an address — that is never a job title, and not ours to keep.
     */
    function safeTerm(raw: string): string {
        const t = raw.trim();
        if (!t || t.includes('@')) return '';
        return t.toLowerCase().slice(0, 80);
    }

    /**
     * The whole of the required-selection behaviour, shared by Enter, the button and the phone
     * keyboard's search key. Returns nothing; the form submits only through its own Enter path,
     * and only once a chip exists.
     *
     * Something typed: make sure the suggestions are for THIS text (the debounce may not have fired,
     * or the reply may not be back — the fast typer's case), then take the highlighted one, or the
     * first. Nothing matches: hold the visitor here and say so, and record the phrase, because this
     * is the one place a title we have no role for becomes visible — the server never sees it now.
     * Nothing typed and nothing picked: say what is needed.
     */
    async function resolveOrExplain() {
        const q = query.trim();
        if (!q) {
            if (!chips.length) hint = 'Pick at least one job title from the list.';
            return;
        }
        if (resolving) return;
        resolving = true;
        try {
            clearTimeout(debounceTimer);
            if (suggestionsFor !== q.toLowerCase()) await fetchSuggestions(query);
            if (visible.length) {
                const s = visible[activeIndex >= 0 && activeIndex < visible.length ? activeIndex : 0];
                addChip(s.code, s.name, q);
                return;
            }
            hint = `We don't have "${q}" yet. Try a shorter word, like "driver", "nurse" or "manager".`;
            open = false;
            analytics.findJobsNoMatch(safeTerm(q));
        } finally {
            resolving = false;
        }
    }

    /**
     * The first suggestion is highlighted as soon as a list exists, so Enter has something to take.
     * Only in selection mode: on the home page a highlighted row would turn a free-text search
     * into a chip the visitor never chose.
     */
    $effect(() => {
        if (requireSelection && visible.length && activeIndex < 0) activeIndex = 0;
    });

    /**
     * The button and the phone keyboard's search key arrive as a form submit, not a keydown, so the
     * gate has to sit on the submit. Capture phase on the document: the page registers its own
     * submit listeners on the form before this island mounts (one disables the button, one counts
     * the submit), and a blocked attempt must reach neither — it was not a submit.
     */
    $effect(() => {
        if (!requireSelection) return;
        const form = inputEl?.form;
        if (!form) return;
        const guard = (e: SubmitEvent) => {
            if (e.target !== form || chips.length) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            void resolveOrExplain();
        };
        document.addEventListener('submit', guard, true);
        return () => document.removeEventListener('submit', guard, true);
    });

    // A bounce-back arrives with the words that failed already in the box. Open the list for them
    // and put the cursor there: the visitor came back to retry, and this is where the retry happens.
    onMount(() => {
        if (requireSelection && query.trim()) {
            open = true;
            inputEl?.focus();
            void fetchSuggestions(query);
        }
    });

    function removeChip(code: string) {
        chips = chips.filter(c => c.code !== code);
        inputEl?.focus();
    }

    function onKeydown(event: KeyboardEvent) {
        if (event.key === 'ArrowDown' && visible.length) {
            event.preventDefault();
            open = true;
            activeIndex = (activeIndex + 1) % visible.length;
        } else if (event.key === 'ArrowUp' && visible.length) {
            event.preventDefault();
            activeIndex = activeIndex <= 0 ? visible.length - 1 : activeIndex - 1;
        } else if (event.key === 'Enter') {
            if (requireSelection) {
                // Enter never submits raw text here. An empty box with a chip picked is the one
                // case that reaches the form; everything else picks, waits, or explains.
                if (!query.trim() && chips.length) return;
                event.preventDefault();
                void resolveOrExplain();
                return;
            }
            // Enter picks the highlighted suggestion; with nothing highlighted it falls through to
            // the form's own submit, so free-text search still works exactly as before.
            if (open && activeIndex >= 0 && visible[activeIndex]) {
                event.preventDefault();
                const s = visible[activeIndex];
                addChip(s.code, s.name, query.trim());
            }
        } else if (event.key === 'Escape') {
            open = false;
            activeIndex = -1;
        } else if (event.key === 'Backspace' && query === '' && chips.length) {
            // Backspace on an empty box removes the last chip — the behaviour every chip input has.
            chips = chips.slice(0, -1);
        }
    }
</script>

<div class="title-search">
    <span class="label">{label}</span>
    <div class="chip-box" bind:this={boxEl}>
        {#each chips as chip (chip.code)}
            <span class="chip">
                {chip.name}
                <button type="button" class="chip-x" aria-label={`Remove ${chip.name}`}
                        onclick={() => removeChip(chip.code)}>×</button>
            </span>
        {/each}
        <input
            bind:this={inputEl}
            type="search"
            name="keyword"
            class="chip-input"
            placeholder={chips.length ? 'add another title' : placeholder}
            autocomplete="off"
            enterkeyhint="search"
            role="combobox"
            aria-expanded={open && visible.length > 0}
            aria-controls="title-suggestions"
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? `title-opt-${activeIndex}` : undefined}
            bind:value={query}
            onfocus={() => { open = true; measureFit(); }}
            oninput={onQueryChanged}
            onkeydown={onKeydown}
            onblur={() => setTimeout(() => { open = false; }, 120)}
        />
    </div>
    {#if hint}
        <p class="ts-hint" role="status" aria-live="polite">{hint}</p>
    {/if}

    {#if open && visible.length}
        <ul class="suggestions" id="title-suggestions" role="listbox"
            style={`max-height:${fitMaxHeight}px`}>
            {#each visible as s, i (s.code)}
                <li
                    id={`title-opt-${i}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    class:active={i === activeIndex}
                    onmousedown={() => addChip(s.code, s.name, query.trim())}
                    onmouseenter={() => (activeIndex = i)}
                >
                    <!-- The matched phrase on its own line; beneath it the disambiguation LinkedIn
                         leaves out — which role that phrase actually resolves to, and which job
                         function it belongs to ("Coordinator · Logistics" vs "Coordinator ·
                         Healthcare"). Stacked so every row aligns on one left edge and no line runs
                         off the side of a phone. -->
                    <span class="s-alias">{s.alias}</span>
                    {#if metaLine(s)}
                        <span class="s-meta">{metaLine(s)}</span>
                    {/if}
                </li>
            {/each}
        </ul>
    {/if}
</div>

<style>
    .title-search { position: relative; display: flex; flex-direction: column; gap: 4px; }
    .label { font-size: 0.85em; }
    /* Colours come from the site's theme tokens in app.css (--rule/--tag-bg/--tag-ink/--ink), NOT
       invented names: an undefined var silently falls back to its light-mode literal, which is how
       a selected chip ended up light-on-light in dark mode. */
    /* This is the one thing the page asks you to do, so it carries weight to match: a 2px --ink
       border rather than a hairline --rule, which left it the lowest-contrast element on a page
       whose heading, band and button are all high-contrast. 44px min-height is the touch-target
       floor. */
    .chip-box {
        display: flex; flex-wrap: wrap; gap: 5px; align-items: center;
        border: 2px solid var(--ink, #222); border-radius: 6px;
        padding: 8px 10px; background: var(--bg, #fff);
        min-height: 44px;
    }
    /* Focus moves the emphasis to the accent, so the field reads as live rather than merely present. */
    .chip-box:focus-within {
        border-color: var(--accent, #b23a1f);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent, #b23a1f) 20%, transparent);
    }
    .chip {
        display: inline-flex; align-items: center; gap: 4px;
        border: 1px solid var(--rule, #ddd); padding: 1px 4px; font-size: 0.9em;
        background: var(--tag-bg, #f1f1f1); color: var(--tag-ink, #555); white-space: nowrap;
    }
    .chip-x { border: 0; background: none; color: inherit; cursor: pointer; font-size: 1.1em; line-height: 1; padding: 0 2px; }
    /* 16px is not cosmetic: iOS Safari zooms the whole page when a focused input is under 16px,
       which would fight the keyboard-space measurement the suggestion list depends on. */
    .chip-input {
        flex: 1 1 8em; min-width: 8em; border: 0; outline: none;
        background: transparent; color: var(--ink, #222); padding: 2px; font-size: 16px;
    }
    .chip-input::placeholder { color: var(--soft, #888); }
    /* Same red the page's own flashes use, so a miss reads as the same kind of message. */
    .ts-hint { margin: 2px 0 0; font-size: 0.9em; color: var(--err-ink, #b23a1f); }
    .suggestions {
        position: absolute; top: 100%; left: 0; right: 0; z-index: 40;
        margin: 2px 0 0; padding: 0; list-style: none; overflow-y: auto;
        border: 1px solid var(--rule, #ddd); background: var(--bg, #fff);
    }
    .suggestions li { padding: 5px 8px; cursor: pointer; font-size: 0.95em; display: flex; flex-direction: column; gap: 1px; }
    .suggestions li.active { background: var(--tag-bg, #f1f1f1); color: var(--tag-ink, #555); }
    .s-meta { opacity: 0.6; font-size: 0.85em; }
</style>
